// components/legal/TermsAndConditions.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RequestASongLogo } from '../components/common/RequestASongLogo';

const TermsAndConditions = ({ userType = 'audience' }) => {
  const navigate = useNavigate();
  
  const isMusician = userType === 'musician';
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <RequestASongLogo className="h-8 w-auto" />
            <button
              onClick={() => navigate(-1)}
              className="text-purple-600 hover:text-purple-800 font-medium"
            >
              ← Back
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Terms and Conditions {isMusician ? 'for Musicians' : 'for Audience Members'}
            </h1>
            <p className="text-gray-600">
              Last Updated: {new Date().toLocaleDateString()}
            </p>
            
            {/* Quick Summary Box */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-blue-900 mb-3">
                📋 Quick Summary
              </h2>
              {isMusician ? (
                <ul className="text-blue-800 space-y-1 text-sm">
                  <li>• Request a Song takes a platform fee from successful transactions (currently 10%)</li>
                  <li>• You earn money based on audience ratings of your performances</li>
                  <li>• Payments are made through digital vouchers/gift cards</li>
                  <li>• You're responsible for having rights to perform requested songs</li>
                  <li>• SMS notifications are required for song requests</li>
                  <li>• Phone verification prevents abuse and protects innocent people from spam</li>
                </ul>
              ) : (
                <ul className="text-blue-800 space-y-1 text-sm">
                  <li>• Your payment is authorized but NOT charged until you rate the performance</li>
                  <li>• If you're not satisfied, you can cancel with no charge</li>
                  <li>• Musicians may not perform every requested song</li>
                  <li>• Your name is shared with musicians for personalized performances</li>
                  <li>• Ratings determine whether payment is processed</li>
                </ul>
              )}
            </div>
          </div>

          {/* Terms Content */}
          <div className="prose prose-lg max-w-none">
            {isMusician ? <MusicianTermsContent /> : <AudienceTermsContent />}
          </div>

          {/* Contact Information */}
          <div className="mt-12 bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Questions about these Terms?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <p><strong>Company:</strong> Jackass Hospitality Group Pty Ltd</p>
                <p><strong>ACN:</strong> 652 087 520</p>
                <p><strong>Location:</strong> Canberra, ACT, Australia</p>
              </div>
              <div>
                <p><strong>Support Email:</strong> support@requestasong.com</p>
                <p><strong>Privacy Email:</strong> privacy@requestasong.com</p>
                <p><strong>Legal Email:</strong> legal@requestasong.com</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

// Musician Terms Content with SMS Anti-Abuse Protection
const MusicianTermsContent = () => (
  <div>
    <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Acceptance of Terms</h2>
    <p className="mb-6">
      By registering as a musician on the Request a Song platform operated by Jackass Hospitality Group Pty Ltd 
      (ACN 652 087 520), you agree to be bound by these Terms and Conditions. If you do not agree to these Terms, 
      do not register or use the Service.
    </p>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Platform Description</h2>
    <p className="mb-4">
      Request a Song is a live music platform that connects musicians with audiences for real-time song requests. 
      The platform includes:
    </p>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li>Real-time song request notifications via SMS</li>
      <li>Payment processing for performed requests</li>
      <li>Rating system based on audience satisfaction</li>
      <li>Digital wallet and earnings management</li>
      <li>Performance analytics and history</li>
    </ul>

    {/* NEW SECTION: SMS Anti-Abuse Protection */}
    <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. SMS Notification System & Anti-Abuse Protection</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">3.1 How SMS Notifications Work</h3>
    <p className="mb-4">
      When fans request songs through your public page, our platform automatically sends SMS notifications to your verified phone number. 
      This ensures you never miss a paid performance opportunity while maintaining the security and integrity of the notification system.
    </p>
    
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <p className="text-blue-800 font-medium mb-2">SMS Delivery Requirements:</p>
      <ul className="text-blue-800 text-sm space-y-1">
        <li>• Phone verification required before receiving SMS notifications</li>
        <li>• Only functional mobile phone numbers that you personally own and control</li>
        <li>• One phone number per musician account</li>
        <li>• Platform tracks delivery status and monitors usage patterns</li>
      </ul>
    </div>

    <h3 className="text-xl font-medium text-gray-700 mb-3">3.2 Mandatory Phone Number Verification</h3>
    <p className="mb-3">
      <strong>Why Required:</strong> Phone verification prevents bad actors from using someone else's phone number without permission, 
      protecting innocent people from unwanted SMS spam and ensuring platform integrity.
    </p>
    
    <div className="mb-4">
      <p className="mb-2"><strong>Verification Process:</strong></p>
      <ol className="list-decimal ml-6 space-y-1 text-sm">
        <li>Enter your personal mobile phone number during account setup</li>
        <li>Receive verification code via SMS within 2 minutes</li>
        <li>Enter code to confirm you own and control this phone number</li>
        <li>Phone number is now authorized to receive song request notifications</li>
      </ol>
    </div>

    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
      <p className="text-yellow-800 font-medium mb-2">Phone Number Security:</p>
      <ul className="text-yellow-800 text-sm space-y-1">
        <li>• Phone numbers cannot be shared or transferred between accounts</li>
        <li>• Changes require re-verification and may need admin approval</li>
        <li>• Your phone number is never displayed publicly or shared with fans</li>
        <li>• Additional verification may be required for suspicious changes</li>
      </ul>
    </div>

    <h3 className="text-xl font-medium text-gray-700 mb-3">3.3 SMS Activity Monitoring & Abuse Detection</h3>
    <p className="mb-4">
      We continuously monitor SMS delivery patterns to detect suspicious activity and protect both musicians and innocent phone number owners from abuse.
    </p>
    
    <div className="mb-4">
      <p className="mb-2"><strong>Volume Monitoring:</strong></p>
      <ul className="list-disc ml-6 space-y-1 text-sm">
        <li><strong>Realistic Volumes:</strong> Single performances typically generate 5-20 song requests</li>
        <li><strong>Suspicious Activity:</strong> 50+ requests in one day triggers automatic review</li>
        <li><strong>Extreme Volume Protection:</strong> 100+ requests from single source automatically blocked pending investigation</li>
        <li><strong>Pattern Analysis:</strong> Unusual spikes in request volume flagged for manual review</li>
      </ul>
    </div>

    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
      <p className="text-red-800 font-medium mb-2">Automatic Protections Include:</p>
      <ul className="text-red-800 text-sm space-y-1">
        <li>• Request rate limiting: Maximum 5 requests per fan per 10-minute period</li>
        <li>• New account monitoring: Enhanced scrutiny for accounts receiving high volumes quickly</li>
        <li>• Geographic consistency checks: Unusual patterns trigger investigation</li>
        <li>• Source verification: Fan accounts must show legitimate engagement patterns</li>
      </ul>
    </div>

    <h3 className="text-xl font-medium text-gray-700 mb-3">3.4 Account Verification Tiers & SMS Limits</h3>
    <p className="mb-3">
      To prevent platform abuse while supporting legitimate musicians, SMS notification limits scale with account verification and activity history.
    </p>
    
    <div className="space-y-4 mb-6">
      <div className="border border-gray-200 rounded-lg p-4">
        <p className="font-medium text-gray-800 mb-2">New Musician Accounts (First 7 Days)</p>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Maximum 20 SMS notifications per day</li>
          <li>• Enhanced monitoring for suspicious patterns</li>
          <li>• Manual review required for high-volume events</li>
          <li>• Automatic upgrade available after successful verification period</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <p className="font-medium text-gray-800 mb-2">Email + Phone Verified Accounts</p>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• 50 SMS notifications per day under normal monitoring</li>
          <li>• Temporary increases available for announced events</li>
          <li>• Priority support for venue-based performances</li>
          <li>• Reduced monitoring after establishing legitimate activity patterns</li>
        </ul>
      </div>
      
      <div className="border border-gray-200 rounded-lg p-4">
        <p className="font-medium text-gray-800 mb-2">Established Musician Accounts (30+ Days)</p>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Up to 100 SMS notifications per day with good standing</li>
          <li>• Event-based temporary increases (festivals, special performances)</li>
          <li>• Minimal monitoring for accounts with consistent legitimate activity</li>
          <li>• Priority customer support and technical assistance</li>
        </ul>
      </div>
    </div>

    <h3 className="text-xl font-medium text-gray-700 mb-3">3.5 Violation Consequences & Appeals</h3>
    <div className="mb-4">
      <p className="mb-2"><strong>Platform Abuse (Using Others' Phone Numbers):</strong></p>
      <ul className="list-disc ml-6 space-y-1 text-sm text-red-600">
        <li>Immediate account suspension pending investigation</li>
        <li>SMS notifications permanently disabled</li>
        <li>Account termination for confirmed violations</li>
        <li>Potential legal action for harassment or impersonation</li>
      </ul>
    </div>

    <div className="mb-4">
      <p className="mb-2"><strong>Suspicious Activity (Unusual Request Patterns):</strong></p>
      <ul className="list-disc ml-6 space-y-1 text-sm text-yellow-600">
        <li>Temporary SMS notification suspension during investigation</li>
        <li>Enhanced monitoring requirements for future activity</li>
        <li>Account limitations until legitimate use verified</li>
        <li>Additional verification requirements for high-volume events</li>
      </ul>
    </div>

    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
      <p className="text-green-800 font-medium mb-2">Appeal Process:</p>
      <ol className="text-green-800 text-sm space-y-1">
        <li>1. Submit detailed explanation of legitimate performance circumstances</li>
        <li>2. Provide evidence of actual live performances (venue confirmation, photos, etc.)</li>
        <li>3. Administrative review within 3-5 business days</li>
        <li>4. Account restoration with appropriate monitoring level</li>
      </ol>
    </div>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Musician Responsibilities</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">4.1 Account Setup and Maintenance</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li>Provide accurate and current contact information</li>
      <li>Maintain an active phone number for SMS notifications that you personally own and control</li>
      <li>Complete phone verification process promptly and honestly</li>
      <li>Keep your song repertoire list updated and accurate</li>
      <li>Respond to platform communications and verification requests promptly</li>
      <li>Report any suspicious activity or potential abuse to platform support</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">4.2 Performance Obligations</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li>Make reasonable efforts to fulfill song requests when possible</li>
      <li>Communicate clearly with requesters about song availability</li>
      <li>Maintain professional conduct during performances</li>
      <li>Respect venue rules and customer requests</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">4.3 Legal and Copyright Compliance</h3>
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
      <p className="text-yellow-800 font-medium mb-2">Important Copyright Notice:</p>
      <ul className="text-yellow-800 text-sm space-y-1">
        <li>• You are responsible for ensuring you have the right to perform all requested songs</li>
        <li>• Venues typically hold APRA AMCOS licenses for live performances</li>
        <li>• You must comply with all applicable copyright and licensing requirements</li>
        <li>• Request a Song is not responsible for your copyright compliance</li>
      </ul>
    </div>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. Payment Terms</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">5.1 Earnings Structure</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li><strong>Base Payment:</strong> Earn money for each successfully performed and rated request</li>
      <li><strong>Rating Bonus:</strong> Higher ratings may result in bonus payments</li>
      <li><strong>Platform Fee:</strong> Request a Song retains a platform fee from each transaction (currently 10%)</li>
      <li><strong>Payment Threshold:</strong> Minimum payout threshold may apply</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">5.2 Payment Processing</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li><strong>Payment Method:</strong> Earnings paid via digital vouchers/gift cards</li>
      <li><strong>Processing Time:</strong> Payments processed after audience rating confirmation</li>
      <li><strong>Failed Requests:</strong> No payment for unperformed or poorly rated requests</li>
      <li><strong>Dispute Resolution:</strong> Payment disputes handled through platform support</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">5.3 Tax Responsibilities</h3>
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <p className="text-blue-800 text-sm">
        <strong>Australian Tax Notice:</strong> You are responsible for reporting earnings as income for tax purposes. 
        Request a Song will provide earnings summaries but does not provide tax advice. 
        Consult a tax professional for guidance on your obligations.
      </p>
    </div>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Platform Usage Rules</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">6.1 Acceptable Use</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li>Use the platform only for legitimate music performance activities</li>
      <li>Maintain respectful communication with all users</li>
      <li>Follow venue policies and local regulations</li>
      <li>Provide honest information about your capabilities and repertoire</li>
      <li>Use only phone numbers you personally own and control</li>
      <li>Comply with SMS anti-abuse monitoring and verification requirements</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">6.2 Prohibited Activities</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li>Using someone else's phone number without permission for SMS notifications</li>
      <li>Attempting to circumvent SMS monitoring or verification systems</li>
      <li>Creating multiple accounts to bypass verification or monitoring limits</li>
      <li>Circumventing the platform for direct payments</li>
      <li>Sharing account access with unauthorized individuals</li>
      <li>Manipulating ratings or payment systems</li>
      <li>Performing copyrighted material without proper rights</li>
      <li>Engaging in discriminatory or offensive behavior</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Account Termination</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">7.1 Termination by You</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li>You may deactivate your account at any time</li>
      <li>Outstanding earnings will be paid according to standard schedules</li>
      <li>Some data may be retained for legal and tax purposes</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">7.2 Termination by Platform</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li>We may suspend accounts for Terms violations, including SMS abuse</li>
      <li>Persistent poor ratings may result in account review</li>
      <li>Copyright violations may result in immediate termination</li>
      <li>Phone number verification violations result in immediate account suspension</li>
      <li>Confirmed use of others' phone numbers results in permanent account termination</li>
      <li>You will be notified of termination reasons when possible</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Liability and Indemnification</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">8.1 Platform Liability Limitations</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li>Request a Song provides the platform "as is" without warranties</li>
      <li>We are not responsible for venue policies or audience behavior</li>
      <li>Platform availability is not guaranteed at all times</li>
      <li>SMS delivery depends on third-party providers and network availability</li>
      <li>Our liability is limited to the platform fees you have paid</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">8.2 Musician Indemnification</h3>
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <p className="text-red-800 text-sm">
        <strong>Important:</strong> You agree to indemnify Request a Song against claims arising from your performances, 
        including copyright violations, property damage, personal injury, or misuse of phone verification systems. 
        Ensure you have appropriate public liability insurance and legal permissions for your performances. 
        You are also responsible for ensuring you only use phone numbers you personally own and control.
      </p>
    </div>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Dispute Resolution</h2>
    <p className="mb-4">
      These Terms are governed by Australian law. Disputes will be resolved through:
    </p>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li><strong>Platform Support:</strong> First attempt resolution through customer support</li>
      <li><strong>SMS Disputes:</strong> Special escalation process for phone verification and abuse claims</li>
      <li><strong>Mediation:</strong> Use of professional mediation services if needed</li>
      <li><strong>Australian Courts:</strong> Legal proceedings in Australian Capital Territory courts</li>
    </ul>
  </div>
);

// Audience Terms Content (unchanged from original)
const AudienceTermsContent = () => (
  <div>
    <h2 className="text-2xl font-semibold text-gray-800 mb-4">1. Acceptance of Terms</h2>
    <p className="mb-6">
      By using the Request a Song platform operated by Jackass Hospitality Group Pty Ltd (ACN 652 087 520) 
      to make song requests, you agree to be bound by these Terms and Conditions. If you do not agree to these Terms, 
      do not use the Service.
    </p>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">2. Platform Description</h2>
    <p className="mb-4">
      Request a Song is a live music platform that allows you to request songs from performing musicians 
      at participating venues. The platform includes:
    </p>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li>Song request submission with personalized messages</li>
      <li>Secure payment authorization system</li>
      <li>Performance rating and feedback system</li>
      <li>Real-time communication with musicians</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">3. How Song Requests Work</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">3.1 Making a Request</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li>Browse available musicians and their repertoires</li>
      <li>Submit song requests with optional personal messages</li>
      <li>Authorize payment amount (you will NOT be charged immediately)</li>
      <li>Your name and message are shared with the musician</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">3.2 Payment Authorization</h3>
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
      <p className="text-green-800 font-medium mb-2">Payment Protection:</p>
      <ul className="text-green-800 text-sm space-y-1">
        <li>• Your payment is authorized but NOT charged when you make a request</li>
        <li>• You are only charged after you rate the performance</li>
        <li>• If you're not satisfied, you can cancel with no charge</li>
        <li>• Your payment method is securely processed by trusted third parties</li>
      </ul>
    </div>

    <h3 className="text-xl font-medium text-gray-700 mb-3">3.3 Performance and Rating</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li>Musicians will attempt to fulfill your request when possible</li>
      <li>You'll be notified when your song is performed or if it's unavailable</li>
      <li>Rate the performance to complete the transaction</li>
      <li>Your rating determines whether payment is processed</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">4. Payment and Refund Policy</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">4.1 Payment Process</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li><strong>Authorization:</strong> Payment authorized when request is submitted</li>
      <li><strong>Hold Period:</strong> Funds held until performance rating is submitted</li>
      <li><strong>Completion:</strong> Payment processed based on your satisfaction rating</li>
      <li><strong>Cancellation:</strong> Request can be canceled before performance</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">4.2 Refund Conditions</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li><strong>Automatic Refunds:</strong> If song is not performed or you rate it poorly</li>
      <li><strong>Technical Issues:</strong> Refunds for platform or payment processing errors</li>
      <li><strong>Venue Problems:</strong> Refunds if venue prevents performance</li>
      <li><strong>Dispute Resolution:</strong> Case-by-case review for disputed charges</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">4.3 Pricing</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li>Prices set by individual musicians or venues</li>
      <li>All prices displayed clearly before payment authorization</li>
      <li>Prices may vary by song complexity, time of day, or demand</li>
      <li>No hidden fees beyond clearly displayed amounts</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">5. User Conduct and Content</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">5.1 Acceptable Requests</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li>Request songs that are appropriate for the venue</li>
      <li>Be respectful in your messages to musicians</li>
      <li>Understand that not all requests can be fulfilled</li>
      <li>Provide accurate contact information if needed</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">5.2 Prohibited Content</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li>Offensive, discriminatory, or inappropriate song requests</li>
      <li>Personal attacks or harassment of musicians</li>
      <li>Requests for illegal or copyrighted material without proper licensing</li>
      <li>Spam or repetitive requests</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">5.3 Privacy and Data Sharing</h3>
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <p className="text-blue-800 text-sm">
        <strong>Information Sharing:</strong> Your name and request message are shared with musicians 
        to enable personalized performances. Your payment information is processed securely and 
        not shared with musicians. See our Privacy Policy for complete details.
      </p>
    </div>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">6. Platform Availability and Limitations</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">6.1 Service Availability</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li>Platform availability depends on venue participation and musician presence</li>
      <li>Not all venues may have participating musicians at all times</li>
      <li>Technical maintenance may temporarily affect service</li>
      <li>Internet connectivity may impact real-time features</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">6.2 No Performance Guarantee</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li>Musicians may not be able to fulfill all requests</li>
      <li>Song availability depends on musician repertoire and venue policies</li>
      <li>Performance quality may vary by musician skill and circumstances</li>
      <li>Venue rules or technical issues may prevent performance</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">7. Liability and Disclaimers</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">7.1 Platform Disclaimers</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li>Request a Song is provided "as is" without warranties</li>
      <li>We do not guarantee specific performance outcomes</li>
      <li>We are not responsible for musician performance quality</li>
      <li>Venue policies and local laws may affect service availability</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">7.2 Limitation of Liability</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li>Our liability is limited to the amount of your specific request</li>
      <li>We are not liable for indirect or consequential damages</li>
      <li>You use the platform at your own risk regarding venue policies</li>
      <li>Some jurisdictions may not allow certain liability limitations</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">8. Account Termination and Data Retention</h2>
    
    <h3 className="text-xl font-medium text-gray-700 mb-3">8.1 Account Termination</h3>
    <ul className="list-disc ml-6 mb-4 space-y-1">
      <li>You can stop using the platform at any time</li>
      <li>We may restrict access for Terms violations</li>
      <li>Outstanding payment authorizations will be handled according to their status</li>
    </ul>

    <h3 className="text-xl font-medium text-gray-700 mb-3">8.2 Data Retention</h3>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li>Request history may be retained for service improvement</li>
      <li>Payment records kept for legal and tax compliance</li>
      <li>Personal information handled according to our Privacy Policy</li>
    </ul>

    <h2 className="text-2xl font-semibold text-gray-800 mb-4">9. Governing Law and Disputes</h2>
    <p className="mb-4">
      These Terms are governed by Australian law. For any disputes:
    </p>
    <ul className="list-disc ml-6 mb-6 space-y-1">
      <li><strong>Customer Support:</strong> Contact support@requestasong.com for assistance</li>
      <li><strong>Escalation:</strong> Unresolved issues may be escalated to management</li>
      <li><strong>Legal Proceedings:</strong> Governed by Australian Capital Territory courts</li>
      <li><strong>Consumer Rights:</strong> Nothing limits your rights under Australian Consumer Law</li>
    </ul>
  </div>
);

export default TermsAndConditions;